import React, { useState } from 'react';
import { Alert, Button, Card, Checkbox, message, Modal, Space, Table, Tag, Tooltip } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';

import { FETCH_TEST_ENVIRONMENTS, FETCH_TEST_SESSIONS } from 'graphql/queries/adminQueries';
import {
  START_TEST_ENVIRONMENTS,
  STOP_TEST_ENVIRONMENT_INSTANCE,
} from 'graphql/mutations/adminMutations';

import './TestEnvironments.scss';

const SESSION_LIMIT = 20;

const stateColor = (instanceState) => {
  if (instanceState === 'running') {
    return 'green';
  }
  if (instanceState === 'pending' || instanceState === 'stopping') {
    return 'orange';
  }
  if (instanceState === 'stopped') {
    return 'red';
  }
  return 'default';
};

const formatDateTime = (value) => (value ? moment(value).format('DD MMM YYYY, HH:mm') : '-');

/**
 * Why Stop now is unavailable, or empty when it is available. Doubles as the
 * disabled flag and the tooltip, so the two can never disagree.
 */
const stopDisabledReason = (instance, isStarting) => {
  if (isStarting) {
    return 'Wait for the start in progress to finish';
  }
  if (instance.instanceState === 'stopped') {
    return 'Already stopped';
  }
  if (instance.instanceState === 'stopping' || instance.instanceState === 'shutting-down') {
    return 'Already stopping';
  }
  return '';
};

const envStatusTag = (status) => {
  if (status === 'running') {
    return <Tag color="green">running</Tag>;
  }
  if (status === 'partial') {
    return <Tag color="orange">partial</Tag>;
  }
  if (status === 'stopped') {
    return <Tag color="default">stopped</Tag>;
  }
  return null;
};

const TestEnvironments = () => {
  const [selectedIds, setSelectedIds] = useState([]);

  const {
    data: environmentsData,
    loading: isFetchingEnvironments,
    refetch: refetchEnvironments,
  } = useQuery(FETCH_TEST_ENVIRONMENTS, { fetchPolicy: 'network-only' });

  const {
    data: sessionsData,
    loading: isFetchingSessions,
    refetch: refetchSessions,
  } = useQuery(FETCH_TEST_SESSIONS, {
    fetchPolicy: 'network-only',
    variables: { limit: SESSION_LIMIT },
  });

  const [startTestEnvironments, { loading: isStarting }] = useMutation(START_TEST_ENVIRONMENTS, {
    onCompleted: async ({ startTestEnvironments: result }) => {
      message.success(
        `Environment(s) ready. Auto-stops at ${formatDateTime(result?.scheduledStopAt)}.`
      );
      setSelectedIds([]);
      await Promise.all([refetchEnvironments(), refetchSessions()]);
    },
    onError: (error) => message.error(error?.message || 'Failed to start the environment(s)'),
  });

  const [stopTestEnvironmentInstance, { loading: isStopping }] = useMutation(
    STOP_TEST_ENVIRONMENT_INSTANCE,
    {
      onCompleted: async () => {
        message.success('Stop requested. The instance will shut down shortly.');
        await refetchEnvironments();
      },
      onError: (error) => message.error(error?.message || 'Failed to stop the instance'),
    }
  );

  const instances = environmentsData?.fetchTestEnvironments?.instances || [];
  const sessions = sessionsData?.fetchTestSessions?.sessions || [];

  const startEnvironments = () => {
    if (!selectedIds.length) {
      return;
    }
    startTestEnvironments({ variables: { environmentIds: selectedIds } });
  };

  const confirmStop = (instance) => {
    Modal.confirm({
      title: `Stop ${instance.label} now?`,
      content:
        'This stops every environment on that instance, including any another tester is using.',
      okText: 'Stop now',
      okButtonProps: { danger: true },
      onOk: () => stopTestEnvironmentInstance({ variables: { instanceKey: instance.key } }),
    });
  };

  const columns = [
    {
      title: 'Tester',
      dataIndex: 'testerName',
      key: 'testerName',
      render: (testerName, session) => testerName || session.tester || '-',
    },
    {
      title: 'Environments',
      dataIndex: 'environmentIds',
      key: 'environmentIds',
      render: (environmentIds) => (environmentIds || []).join(', '),
    },
    {
      title: 'Started at',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: formatDateTime,
    },
    {
      title: 'Scheduled stop',
      dataIndex: 'scheduledStopAt',
      key: 'scheduledStopAt',
      render: formatDateTime,
    },
  ];

  return (
    <div className="admin-wrapper test-environments">
      {instances.map((instance) => (
        <Card
          key={instance.key}
          title={instance.label}
          loading={isFetchingEnvironments}
          extra={
            <Space>
              <Button onClick={() => refetchEnvironments()} disabled={isStarting}>
                Refresh
              </Button>
              <Tooltip title={stopDisabledReason(instance, isStarting)}>
                {/* A disabled button fires no mouse events, so the tooltip
                    needs an enabled element to hang off. */}
                <span className="stop-button-wrapper">
                  <Button
                    danger
                    onClick={() => confirmStop(instance)}
                    loading={isStopping}
                    disabled={!!stopDisabledReason(instance, isStarting)}>
                    Stop now
                  </Button>
                </span>
              </Tooltip>
            </Space>
          }>
          <Space direction="vertical" size="small" className="instance-body">
            <div>
              Status:{' '}
              <Tag color={stateColor(instance.instanceState)}>
                {instance.instanceState || 'unknown'}
              </Tag>
              {instance.proxyState ? (
                <>
                  nginx proxy:{' '}
                  <Tag color={instance.proxyState === 'running' ? 'green' : 'red'}>
                    {instance.proxyState}
                  </Tag>
                </>
              ) : null}
            </div>
            <div>Scheduled stop: {formatDateTime(instance.scheduledStopAt)}</div>

            {instance.proxyState && instance.proxyState !== 'running' ? (
              <Alert
                type="error"
                showIcon
                message="The nginx proxy is not running, so no environment on this instance is reachable even if its containers are up."
                description="nginx resolves every upstream at startup and exits if one is missing. It needs a resolver directive with variable proxy_pass so it tolerates stopped environments."
              />
            ) : null}

            <Checkbox.Group
              className="environment-list"
              value={selectedIds}
              onChange={(checked) =>
                setSelectedIds((current) => {
                  const otherInstanceIds = current.filter(
                    (id) => !instance.environments.some((env) => env.id === id)
                  );
                  return [...otherInstanceIds, ...checked];
                })
              }
              disabled={isStarting}
              options={instance.environments.map((environment) => ({
                value: environment.id,
                label: (
                  <span className="environment-option">
                    {environment.label}
                    {envStatusTag(environment.status)}
                    {environment.status === 'partial' ? (
                      <span className="environment-detail">
                        {(environment.services || [])
                          .filter((service) => service.state !== 'running')
                          .map((service) => `${service.name}: ${service.state}`)
                          .join(', ')}
                      </span>
                    ) : null}
                  </span>
                ),
              }))}
            />
          </Space>
        </Card>
      ))}

      <Card title="Start environments">
        <Button
          type="primary"
          onClick={startEnvironments}
          loading={isStarting}
          disabled={!selectedIds.length}>
          {isStarting ? 'Starting environment(s)...' : 'Start testing'}
        </Button>

        {isStarting ? (
          <Alert
            className="starting-note"
            type="info"
            showIcon
            message="A cold start can take a couple of minutes while the instance boots and the processes come up. Keep this tab open."
          />
        ) : null}
      </Card>

      <Card title="Recent sessions">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={sessions}
          loading={isFetchingSessions}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default TestEnvironments;
